import { z } from 'zod';
import { request, useMutation, useQuery, useQueryClient } from '@applyboard/ui-utils';
import { refetchType } from '..';
import { NewVasError } from '../../common/errors/NewVasError';

const NOTIFICATIONS_V1_ROOT_URL = '/api/notifications/v1';

const NotificationSchema = z.object({
  id: z.string().trim(),
  rule_name: z.string().trim(),
  created_at: z.string().trim(),
});

const RawNotificationsSchema = z.array(NotificationSchema);

export type RawNotifications = z.infer<typeof RawNotificationsSchema>;
export type Notification = z.infer<typeof NotificationSchema>;

export function useGetNotifications(
  options: { enabled?: boolean; retryCount: number; retryOnMount?: boolean } = { retryCount: 2 },
) {
  const { isLoading, isFetching, error, data, refetch } = useQuery(
    ['useGetNotification', { pkg: 'ab-vas-gic' }],
    async () => {
      const response = await request<RawNotifications>(NOTIFICATIONS_V1_ROOT_URL, {
        schema: RawNotificationsSchema,
        allowAnonymous: false,
      });

      return response as Notification[];
    },
    {
      enabled: options.enabled,
      refetchOnMount: false,
      retryOnMount: options.retryOnMount,
      retry: options.retryCount,
    },
  );

  return {
    notifications: data as Notification[],
    notificationError: error,
    isLoadingNotifications: isLoading,
    isFetchingNotifications: isFetching,
    refetchNotifications: refetch as refetchType,
  };
}

export function useArchiveNotification() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError, mutateAsync } = useMutation(async (notificationId: string) =>
    request(`${NOTIFICATIONS_V1_ROOT_URL}/${notificationId}`, {
      method: 'DELETE',
      allowAnonymous: false,
      schema: z.null(),
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new NewVasError(jsonMessage, 'Dismiss notification failed');
      },
    }),
  );

  const archiveNotification = (
    notificationId: string,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(notificationId, {
      onSuccess: async () => {
        queryClient.invalidateQueries(['useGetNotification', { pkg: 'ab-vas-gic' }]);
        onSuccess?.();
      },
      onError: (error: unknown) => onError?.(error),
    });
  };

  const archiveNotificationAsync = async (notificationId: string) => {
    await mutateAsync(notificationId);
    queryClient.invalidateQueries(['useGetNotification', { pkg: 'ab-vas-gic' }]);
  };

  return {
    archiveNotification,
    archiveNotificationAsync,
    isDismissingNotification: isLoading,
    isDimissingNotificationError: isError,
  };
}
